import {
    Container,
    cssVar,
    Divider,
    getSize,
    Header,
    Label,
    PrimaryButton,
    useAlignments,
    useScreen,
    useSizes,
} from 'metrix-common3';
// biome-ignore lint/correctness/noUnusedImports: required
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import AdjustmentCard from './AdjustmentCard';
import AdjustmentModal from './AdjustmentModal';

const SectionHeaderWrapper = styled.div`
    margin-top: ${getSize('sm')}px;
    margin-bottom: ${getSize('sm')}px;
`;

const TotalRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 16px;
    gap: 24px;
    border-top: 2px solid ${cssVar.colorStrokeDark};
`;

const TotalLabelArea = styled.div`
    flex: ${({ $mobile }) => ($mobile ? '0 0 auto' : '1')};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const TotalValueArea = styled.div`
    flex: 1;
    display: flex;
    justify-content: flex-end;
`;

const TotalActionSpacer = styled.div`
    width: 80px;
    min-width: 80px;
`;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: ${getSize('md')}px;
    margin-bottom: ${getSize('lg')}px;
    padding-right: 80px;
`;

const SuccessIndicator = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 44px;
    padding: 0 24px;
    background-color: transparent;
    border-radius: 4px;
`;

const ProfileUnderwriterCoverageAdjustment = ({
    policyAdjustments,
    context,
    doGetPolicyAdjustments,
    doApplyPolicyAdjustment,
    doApplyPolicyAdjustmentChanges,
    doDeletePolicyAdjustment,
}) => {
    const alignments = useAlignments();
    const sizes = useSizes();
    const { isMobile: mobile } = useScreen();

    const [modalState, setModalState] = useState({
        visible: false,
        mode: 'add',
        adjustment: null,
        adjustmentType: null,
        parentId: null,
        parentType: null,
        parentItem: null,
    });

    useEffect(() => {
        doGetPolicyAdjustments();
    }, [doGetPolicyAdjustments]);

    // Track if we submitted the form (started loading)
    const [pendingSave, setPendingSave] = useState(false);

    // Track when Apply Changes was successful (for showing success indicator)
    const [showAppliedSuccess, setShowAppliedSuccess] = useState(false);

    // Track the last completed operation to handle timing issues
    const [lastCompletedOperation, setLastCompletedOperation] = useState(null);

    const handleModalClose = useCallback(() => {
        setPendingSave(false); // Reset pending save flag when closing modal
        setModalState({
            visible: false,
            mode: 'add',
            adjustment: null,
            adjustmentType: null,
            parentId: null,
            parentType: null,
            parentItem: null,
        });
    }, []);

    // Watch for loading to start - this means we submitted
    useEffect(() => {
        const isLoading = Array.isArray(context?.loading)
            ? context.loading.some((x) => x.code === 'applyPolicyAdjustment' && x.value === true)
            : context?.loading;
        if (isLoading && modalState.visible) {
            setPendingSave(true);
        }
    }, [context?.loading, modalState.visible]);

    // Close modal when pending save completes successfully
    useEffect(() => {
        const isLoadingAdjustment = Array.isArray(context?.loading)
            ? context.loading.some((x) => x.code === 'applyPolicyAdjustment' && x.value === true)
            : context?.loading;
        if (
            modalState.visible &&
            pendingSave &&
            !isLoadingAdjustment &&
            context?.operation?.code === 'applyPolicyAdjustment' &&
            context?.operation?.status === true
        ) {
            setPendingSave(false);
            handleModalClose();
        }

        // Reset pendingSave when loading completes with error
        if (
            modalState.visible &&
            pendingSave &&
            !isLoadingAdjustment &&
            context?.operation?.code === 'applyPolicyAdjustment' &&
            context?.operation?.status === false
        ) {
            setPendingSave(false);
        }
    }, [
        context?.operation?.code,
        context?.operation?.status,
        context?.loading,
        modalState.visible,
        pendingSave,
        handleModalClose,
    ]);

    // Watch for successful Apply Changes operation
    useEffect(() => {
        // Capture the operation when it completes (regardless of loading state)
        if (context?.operation?.code === 'applyPolicyAdjustmentChanges' && context?.operation?.status === true) {
            setLastCompletedOperation({
                code: context.operation.code,
                status: context.operation.status,
                timestamp: Date.now(),
            });
        }
    }, [context?.operation?.code, context?.operation?.status]);

    // Show banner when loading finishes after successful operation
    useEffect(() => {
        const isLoadingApplyChanges = Array.isArray(context?.loading)
            ? context.loading.some((x) => x.code === 'applyPolicyAdjustmentChanges' && x.value === true)
            : context?.loading;

        // Show banner when loading finishes and we have a successful operation in our state
        if (
            !isLoadingApplyChanges &&
            lastCompletedOperation?.code === 'applyPolicyAdjustmentChanges' &&
            lastCompletedOperation?.status === true
        ) {
            setShowAppliedSuccess(true);

            // Clear the completed operation and hide banner after 3 seconds
            const timer = setTimeout(() => {
                setShowAppliedSuccess(false);
                setLastCompletedOperation(null);
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [context?.loading, lastCompletedOperation]);

    const readOnly = policyAdjustments?.coverageDetails?.isReadonly;

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount);
    };

    const formatPercentage = (value) => {
        return `${value > 0 ? '' : ''}${value}%`;
    };

    const formatValue = (adjustment) => {
        // Use adjustmentAmountFormatted if available, otherwise format the value
        if (adjustment.adjustmentAmountFormatted) {
            return adjustment.adjustmentAmountFormatted;
        }
        if (adjustment.adjustmentMethod === 'percentage') {
            return formatPercentage(adjustment.adjustmentMethodValue);
        }
        // For both flatAmount and absoluteAmount, display as currency
        return formatCurrency(Math.abs(adjustment.adjustmentMethodValue || 0));
    };

    const hasChanges = () => {
        if (!data) return false;

        // Check all sections for any changes
        const allItems = [
            ...(data.premiums || []),
            ...(data.fees || []),
            ...(data.discounts || []),
            ...(data.surcharges || []),
        ];

        // Check if any item or its nested adjustments have changes
        for (const item of allItems) {
            // Check if the item itself is new, edited, or deleted
            if (item.changeTracking && item.changeTracking !== 'noChange') {
                return true;
            }
            // Check nested adjustments
            if (item.adjustments && item.adjustments.length > 0) {
                for (const adj of item.adjustments) {
                    if (adj.changeTracking && adj.changeTracking !== 'noChange') {
                        return true;
                    }
                }
            }
        }

        return false;
    };

    const handleEdit = (item, type, parentId, parentType, parentItem) => {
        setModalState({
            visible: true,
            mode: 'edit',
            adjustment: item,
            adjustmentType: type,
            parentId,
            parentType,
            parentItem,
        });
    };

    const handleDelete = (item, type, parentId, parentType) => {
        // Map plural forms to singular for API
        const coverageTypeMap = {
            premium: 'premium',
            premiums: 'premium',
            fee: 'fee',
            fees: 'fee',
            discount: 'discount',
            discounts: 'discount',
            surcharge: 'surcharge',
            surcharges: 'surcharge',
        };

        // Build adjustment object for deletion
        const adjustment = {
            adjustmentGuid: item.adjustmentGuid,
            coverageItemType: parentType
                ? coverageTypeMap[parentType] || item.coverageItemType || parentType
                : undefined,
            coverageItemGuid: item.coverageItemGuid || parentId,
            policyTransactionGuid: policyAdjustments.coverageDetails.policyTransactionGuid,
            coverageType: !parentType ? coverageTypeMap[type] || item.coverageType || type : undefined,
            source: item.source,
        };

        const payload = {
            adjustment,
            coverageDetails: policyAdjustments.coverageDetails,
        };

        doDeletePolicyAdjustment(payload);
    };

    const handleAdd = (type, parentId, parentType, parentItem) => {
        setModalState({
            visible: true,
            mode: 'add',
            adjustment: null,
            adjustmentType: type,
            parentId,
            parentType,
            parentItem,
        });
    };

    const handleModalSave = (savedAdjustment) => {
        const payload = {
            adjustment: savedAdjustment,
            coverageDetails: policyAdjustments.coverageDetails,
        };
        doApplyPolicyAdjustment(payload);
        // Don't close modal immediately - wait for operation result
        // Modal will auto-close when context.operation.code === 'applyPolicyAdjustment' && status === true
    };

    const renderAdjustmentRow = (adjustment, parentType, parentId, parentItem) => {
        const displayValue = formatValue(adjustment);

        return (
            <AdjustmentCard
                key={adjustment.code}
                label={adjustment.description}
                value={displayValue}
                nested
                onEdit={
                    !readOnly
                        ? () => handleEdit(adjustment, `${parentType}Adjustment`, parentId, parentType, parentItem)
                        : undefined
                }
                onDelete={
                    !readOnly
                        ? () => handleDelete(adjustment, `${parentType}Adjustment`, parentId, parentType)
                        : undefined
                }
            />
        );
    };

    const renderPremiumLine = (premium) => (
        <div key={premium.coverageItemGuid}>
            <AdjustmentCard
                label={premium.description}
                value={premium.amountFormatted || formatCurrency(premium.amount)}
                onAdd={
                    !readOnly
                        ? () => handleAdd('premiumAdjustment', premium.coverageItemGuid, 'premiums', premium)
                        : undefined
                }
            />
            {premium.adjustments
                ?.filter((adj) => adj.changeTracking !== 'deleted')
                .map((adj) => renderAdjustmentRow(adj, 'premiums', premium.coverageItemGuid, premium))}
        </div>
    );

    const renderFeeRow = (fee) => (
        <div key={fee.coverageItemGuid}>
            <AdjustmentCard
                label={fee.description}
                value={fee.amountFormatted || formatCurrency(fee.amount)}
                onAdd={!readOnly ? () => handleAdd('feeAdjustment', fee.coverageItemGuid, 'fees', fee) : undefined}
            />
            {fee.adjustments
                ?.filter((adj) => adj.changeTracking !== 'deleted')
                .map((adj) => renderAdjustmentRow(adj, 'fees', fee.coverageItemGuid, fee))}
        </div>
    );

    const renderDiscountRow = (discount) => (
        <div key={discount.coverageItemGuid}>
            <AdjustmentCard
                label={discount.description}
                value={discount.amountFormatted || formatCurrency(discount.amount)}
                onAdd={
                    !readOnly
                        ? () => handleAdd('discountAdjustment', discount.coverageItemGuid, 'discounts', discount)
                        : undefined
                }
            />
            {discount.adjustments
                ?.filter((adj) => adj.changeTracking !== 'deleted')
                .map((adj) => renderAdjustmentRow(adj, 'discounts', discount.coverageItemGuid, discount))}
        </div>
    );

    const renderSurchargeRow = (surcharge) => (
        <div key={surcharge.coverageItemGuid}>
            <AdjustmentCard
                label={surcharge.description}
                value={surcharge.amountFormatted || formatCurrency(surcharge.amount)}
                onAdd={
                    !readOnly
                        ? () => handleAdd('surchargeAdjustment', surcharge.coverageItemGuid, 'surcharges', surcharge)
                        : undefined
                }
            />
            {surcharge.adjustments
                ?.filter((adj) => adj.changeTracking !== 'deleted')
                .map((adj) => renderAdjustmentRow(adj, 'surcharges', surcharge.coverageItemGuid, surcharge))}
        </div>
    );

    const data = policyAdjustments?.coverageDetails;

    if (!data) {
        return null;
    }

    // Check if there's an error from the operation in context
    const hasError = context?.operation?.errors && context.operation.errors.length > 0;

    return (
        <Container horizontalAlignment={alignments.left}>
            <Divider top={sizes.xl} />
            <Header
                size={sizes.lg}
                textAlign={alignments.left}
                text={<FormattedMessage id='Profile_UnderwriterCoverageAdjustment.header' />}
            />

            {hasError && (
                <Container top={sizes.md} bottom={sizes.md}>
                    {context.operation.errors.map((error) => (
                        <Label
                            key={error.code || error.value}
                            text={error.value || error.code}
                            size={sizes.lg}
                            textAlign={alignments.left}
                            color={cssVar.colorError}
                        />
                    ))}
                </Container>
            )}

            {!hasError && (
                <>
                    {/* Premiums Section */}
                    <SectionHeaderWrapper>
                        <Label
                            text={<FormattedMessage id='Profile_UnderwriterCoverageAdjustment.premiums' />}
                            bold
                            size={sizes.lg}
                            textAlign={alignments.left}
                        />
                    </SectionHeaderWrapper>
                    {data.premiums.length === 0 ? (
                        <AdjustmentCard
                            isEmpty
                            onAdd={!readOnly ? () => handleAdd('premium', null, null) : undefined}
                        />
                    ) : (
                        data.premiums.map(renderPremiumLine)
                    )}

                    {/* Fees Section */}
                    <SectionHeaderWrapper>
                        <Label
                            text={<FormattedMessage id='Profile_UnderwriterCoverageAdjustment.fees' />}
                            bold
                            size={sizes.lg}
                            textAlign={alignments.left}
                        />
                    </SectionHeaderWrapper>
                    {data.fees.length === 0 ? (
                        <AdjustmentCard isEmpty onAdd={!readOnly ? () => handleAdd('fees', null, null) : undefined} />
                    ) : (
                        data.fees.map(renderFeeRow)
                    )}

                    {/* Discounts Section */}
                    <SectionHeaderWrapper>
                        <Label
                            text={<FormattedMessage id='Profile_UnderwriterCoverageAdjustment.discounts' />}
                            bold
                            size={sizes.lg}
                            textAlign={alignments.left}
                        />
                    </SectionHeaderWrapper>
                    {data.discounts.length === 0 ? (
                        <AdjustmentCard
                            isEmpty
                            onAdd={!readOnly ? () => handleAdd('discounts', null, null) : undefined}
                        />
                    ) : (
                        data.discounts.map(renderDiscountRow)
                    )}

                    {/* Surcharges Section */}
                    <SectionHeaderWrapper>
                        <Label
                            text={<FormattedMessage id='Profile_UnderwriterCoverageAdjustment.surcharge' />}
                            bold
                            size={sizes.lg}
                            textAlign={alignments.left}
                        />
                    </SectionHeaderWrapper>
                    {data.surcharges.length === 0 ? (
                        <AdjustmentCard
                            isEmpty
                            onAdd={!readOnly ? () => handleAdd('surcharges', null, null) : undefined}
                        />
                    ) : (
                        data.surcharges.map(renderSurchargeRow)
                    )}

                    {/* Totals Section */}
                    <Container top={sizes.md}>
                        <TotalRow>
                            <TotalLabelArea $mobile={mobile}>
                                <Label text='Subtotal' size={sizes.lg} textAlign={alignments.left} />
                            </TotalLabelArea>
                            <TotalValueArea>
                                <Label
                                    text={data.summary.subtotalFormatted || formatCurrency(data.summary.subtotal)}
                                    textAlign={alignments.right}
                                    size='custom'
                                    fontSize='22'
                                    fontWeight='700'
                                    lineHeight='110%'
                                />
                            </TotalValueArea>
                            <TotalActionSpacer />
                        </TotalRow>
                        <TotalRow>
                            <TotalLabelArea $mobile={mobile}>
                                <Label
                                    text={`Taxes (${data.summary.taxRateFormatted || `${data.summary.taxRate * 100}%`})`}
                                    size={sizes.lg}
                                    textAlign={alignments.left}
                                />
                            </TotalLabelArea>
                            <TotalValueArea>
                                <Label
                                    text={data.summary.taxAmountFormatted || formatCurrency(data.summary.taxAmount)}
                                    textAlign={alignments.right}
                                    size='custom'
                                    fontSize='22'
                                    fontWeight='700'
                                    lineHeight='110%'
                                />
                            </TotalValueArea>
                            <TotalActionSpacer />
                        </TotalRow>
                        <TotalRow>
                            <TotalLabelArea $mobile={mobile}>
                                <Label text='Total' size={sizes.lg} textAlign={alignments.left} bold />
                            </TotalLabelArea>
                            <TotalValueArea>
                                <Label
                                    text={data.summary.grandTotalFormatted || formatCurrency(data.summary.grandTotal)}
                                    textAlign={alignments.right}
                                    size='custom'
                                    fontSize='22'
                                    fontWeight='700'
                                    lineHeight='110%'
                                    bold
                                />
                            </TotalValueArea>
                            <TotalActionSpacer />
                        </TotalRow>
                    </Container>

                    {/* Apply Button / Success Indicator */}
                    {!readOnly && (
                        <ButtonWrapper>
                            {showAppliedSuccess ? (
                                <SuccessIndicator>
                                    <span style={{ fontSize: '20px', color: cssVar.colorSuccess }}>✓</span>
                                    <Label
                                        text={<FormattedMessage id='Profile_UnderwriterCoverageAdjustment.applied' />}
                                        color={cssVar.colorSuccess}
                                        size={sizes.lg}
                                    />
                                </SuccessIndicator>
                            ) : (
                                <PrimaryButton
                                    text={<FormattedMessage id='Profile_UnderwriterCoverageAdjustment.apply' />}
                                    disabled={!hasChanges()}
                                    clickEvent={() => {
                                        const payload = {
                                            coverageDetails: policyAdjustments.coverageDetails,
                                        };
                                        doApplyPolicyAdjustmentChanges(payload);
                                    }}
                                />
                            )}
                        </ButtonWrapper>
                    )}
                </>
            )}

            {/* Adjustment Modal */}
            <AdjustmentModal
                visible={modalState.visible}
                mode={modalState.mode}
                adjustment={modalState.adjustment}
                adjustmentType={modalState.adjustmentType}
                parentId={modalState.parentId}
                parentType={modalState.parentType}
                parentItem={modalState.parentItem}
                policyTransactionGuid={data.policyTransactionGuid}
                initRule={data.initRule}
                validationRule={data.validationRule}
                context={context}
                pendingSave={pendingSave}
                onClose={handleModalClose}
                onSave={handleModalSave}
            />
        </Container>
    );
};

export default ProfileUnderwriterCoverageAdjustment;
